import { setInterfaceMode } from '../store/reducers/interface-mode-reducer';
import { setIsLoginModalOpen } from '../store/reducers/global-reducer';
import {
    readStoredInterfacePreference,
    inferInterfaceModeFromAuth,
    writeStoredInterfaceMode,
    INTERFACE_MODES,
} from './interface-mode';

/**
 * Updates Redux interface mode and persists to localStorage.
 * Does not navigate or change authentication state.
 */
export function applyInterfaceModeChange(dispatch, mode) {
    if (mode !== INTERFACE_MODES.BUYER && mode !== INTERFACE_MODES.SELLER) {
        return;
    }
    dispatch(setInterfaceMode({ mode }));
    writeStoredInterfaceMode(mode);
}

/**
 * Resolves the existing app route for a mode switch using current client auth state.
 * Uses existing login pages — no backend calls.
 */
export function resolvePathForInterfaceMode(mode, { isLoggedIn, userInfo }) {
    if (mode === INTERFACE_MODES.SELLER) {
        if (isLoggedIn && userInfo && userInfo.role === 'seller') {
            return '/seller/dashboard';
        }
        return '/seller/login';
    }
    return '/';
}

/**
 * True when switching to buyer mode without an active buyer (user) session.
 * Mirrors seller mode sending non-sellers to /seller/login.
 */
export function shouldOpenBuyerLoginModal(mode, { isLoggedIn, userInfo }) {
    if (mode !== INTERFACE_MODES.BUYER) {
        return false;
    }
    return !(isLoggedIn && userInfo && userInfo.role === 'user');
}

/**
 * Applies boot-time interface experience without writing localStorage.
 */
function applyBootInterfaceExperience({
    dispatch,
    history,
    mode,
    authState,
    openBuyerLoginModal = false,
}) {
    dispatch(setInterfaceMode({ mode }));

    const path = resolvePathForInterfaceMode(mode, authState);
    if (path !== history.location.pathname) {
        history.replace(path);
    }

    if (openBuyerLoginModal && shouldOpenBuyerLoginModal(mode, authState)) {
        dispatch(setIsLoginModalOpen({ isLoginModalOpen: true }));
        document.body.style.overflow = 'hidden';
    }
}

/**
 * Applies mode change, navigates, and opens buyer login modal when required.
 * Used by the profile toggle — persists explicit user preference.
 */
export function performInterfaceModeSwitch({
    dispatch,
    history,
    targetMode,
    authState,
    replace = false,
    onAfterNavigate,
}) {
    applyInterfaceModeChange(dispatch, targetMode);

    const path = resolvePathForInterfaceMode(targetMode, authState);
    const openBuyerLogin = shouldOpenBuyerLoginModal(targetMode, authState);

    if (path !== history.location.pathname) {
        if (replace) {
            history.replace(path);
        } else {
            history.push(path);
        }
    }

    if (openBuyerLogin) {
        dispatch(setIsLoginModalOpen({ isLoginModalOpen: true }));
        document.body.style.overflow = 'hidden';
    }

    if (onAfterNavigate) {
        onAfterNavigate();
    }
}

/**
 * Restores interface mode on cold launch at the neutral buyer entry route only.
 * Explicit preference is respected exactly; missing preference is inferred from session.
 */
export function restoreInterfaceModeOnBoot({ pathname, dispatch, history, authState }) {
    if (pathname !== '/') {
        return;
    }

    const storedPreference = readStoredInterfacePreference();

    if (storedPreference !== null) {
        if (storedPreference === INTERFACE_MODES.SELLER) {
            applyBootInterfaceExperience({
                dispatch,
                history,
                mode: INTERFACE_MODES.SELLER,
                authState,
            });
            return;
        }

        if (authState.isLoggedIn && authState.userInfo && authState.userInfo.role === 'user') {
            dispatch(setInterfaceMode({ mode: INTERFACE_MODES.BUYER }));
            return;
        }

        applyBootInterfaceExperience({
            dispatch,
            history,
            mode: INTERFACE_MODES.BUYER,
            authState,
            openBuyerLoginModal: true,
        });
        return;
    }

    const inferredMode = inferInterfaceModeFromAuth(authState);

    if (inferredMode === INTERFACE_MODES.SELLER) {
        applyBootInterfaceExperience({
            dispatch,
            history,
            mode: INTERFACE_MODES.SELLER,
            authState,
        });
        return;
    }

    dispatch(setInterfaceMode({ mode: INTERFACE_MODES.BUYER }));
}

/**
 * Returns true when localStorage/Redux reflects an authenticated seller session.
 */
export function isAuthenticatedSeller(authState) {
    const { isLoggedIn, userInfo } = authState;
    return Boolean(isLoggedIn && userInfo && userInfo.role === 'seller');
}
